import { useId } from 'react';

import { Button } from '@/design-system';

import { surveyCopy } from '../copy';
import { MAX_MULTI_INSTANCES } from '../engine';

export interface MultiCountCardProps {
    /** "Hoeveel balkons of dakterrassen zijn er?" uit de template, of een afgeleide vraag. */
    label: string;
    count: number;
    answered: boolean;
    readOnly?: boolean;
    onChange: (count: number) => void;
}

/**
 * De aantalsvraag van een multi-container. Elke instantie krijgt in de boom
 * zijn eigen onderdelen en vragen. De knoppen zijn 44 px, de hele knop is de
 * hitbox.
 */
export function MultiCountCard({
    label,
    count,
    answered,
    readOnly = false,
    onChange,
}: MultiCountCardProps) {
    const labelId = useId();

    return (
        <article
            data-testid="multi-count"
            data-answered={answered ? 'true' : 'false'}
            aria-labelledby={labelId}
            className="overflow-hidden border-t border-l-[3px] border-t-[var(--border-subtle)] border-l-[var(--border-subtle)] bg-[var(--surface-card)]"
            style={{ borderColor: answered ? 'var(--accent-border)' : 'var(--border-default)' }}
        >
            <div className="border-b border-[var(--border-subtle)] bg-[var(--surface-question-head)] px-[10px] py-[5px]">
                <p
                    id={labelId}
                    className="m-0 text-[14px] leading-[1.25] font-semibold text-pretty text-[var(--text-primary)]"
                >
                    {label}
                </p>
                <p className="m-0 mt-[3px] text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {surveyCopy.multi.hint}
                </p>
            </div>
            <div className="flex items-center gap-3 px-[10px] py-[3px]">
                <Button
                    variant="outline"
                    size="md"
                    className="min-w-[44px] px-0 text-[18px]"
                    aria-label={surveyCopy.multi.decrease}
                    disabled={readOnly || count <= 0}
                    onClick={() => onChange(count - 1)}
                >
                    −
                </Button>
                <output
                    aria-live="polite"
                    aria-labelledby={labelId}
                    className="min-w-[44px] text-center font-[family-name:var(--font-mono)] text-[18px] leading-[44px] text-[var(--text-primary)]"
                >
                    {count}
                </output>
                <Button
                    variant="outline"
                    size="md"
                    className="min-w-[44px] px-0 text-[18px]"
                    aria-label={surveyCopy.multi.increase}
                    disabled={readOnly || count >= MAX_MULTI_INSTANCES}
                    onClick={() => onChange(count + 1)}
                >
                    +
                </Button>
            </div>
        </article>
    );
}
